import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Panel, Overline, PageHead, C, fmt } from "@/components/kit";

const INPUTS = [
  ["bins", "Smart bins deployed", 20, 500, 5, 63, ""],
  ["sensorCost", "Sensor + enclosure / bin", 1500, 6000, 100, 2800, "₹"],
  ["trips", "Truck trips / day", 2, 10, 1, 4, ""],
  ["costKm", "Fuel + ops cost / km", 20, 80, 1, 38, "₹"],
  ["reduction", "Route distance saved", 10, 60, 1, 40, "%"],
  ["recKg", "Recyclables recovered / day", 100, 2000, 10, 420, "kg"],
  ["price", "Avg resale price / kg", 5, 40, 1, 14, "₹"],
  ["staffHrs", "Staff hours saved / day", 2, 20, 1, 6, "h"],
  ["wage", "Staff cost / hour", 100, 400, 10, 180, "₹"],
  ["opex", "Platform opex / month", 5000, 50000, 1000, 12000, "₹"],
];
const KM_PER_TRIP = 18;
const SETUP = 150000;

function calc(v) {
  const capex = v.bins * v.sensorCost + SETUP;
  const fuel = v.trips * KM_PER_TRIP * (v.reduction / 100) * v.costKm * 30;
  const resale = v.recKg * v.price * 30 * 0.35;
  const labour = v.staffHrs * v.wage * 30;
  const landfill = v.recKg * 2 * 30;
  const gross = fuel + resale + labour + landfill;
  const net = gross - v.opex;
  const payback = net > 0 ? capex / net : Infinity;
  const series = Array.from({ length: 61 }, (_, m) => ({ month: m, cum: Math.round(-capex + net * m) }));
  return { capex, fuel, resale, labour, landfill, gross, net, payback, series, fiveYr: net * 60 - capex };
}

const ROADMAP = [
  ["Phase 1", "Pilot", "10 zones / 63 bins", 1],
  ["Phase 2", "Full campus", "All hostels + academic blocks", 3.5],
  ["Phase 3", "Multi-campus", "25 institutions, shared MRF", 25 * 3.5],
  ["Phase 4", "Municipal wards", "City SaaS + recycler marketplace", 200],
];

function Slider({ def, value, onChange }) {
  const [k, label, min, max, step, , unit] = def;
  return (
    <div data-testid={`roi-input-${k}`}>
      <div className="flex justify-between text-sm mb-3"><span className="text-mute">{label}</span>
        <span className="font-mono text-txt tabular-nums">{unit === "₹" ? `₹${fmt(value)}` : `${fmt(value)}${unit ? ` ${unit}` : ""}`}</span></div>
      <input type="range" className="cl-range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(k, Number(e.target.value))} data-testid={`roi-slider-${k}`} />
    </div>
  );
}

export default function Roi() {
  const [v, setV] = useState(Object.fromEntries(INPUTS.map((d) => [d[0], d[5]])));
  const r = useMemo(() => calc(v), [v]);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const breakdown = [["Route fuel & ops", r.fuel, C.volt], ["Recycler resale uplift", r.resale, C.rec], ["Labour hours", r.labour, C.org], ["Landfill tipping avoided", r.landfill, C.amber]];

  return (
    <div data-testid="roi-page">
      <PageHead kicker="05 / Judge's ROI & scaling" title="The numbers, stress-tested." sub="Drag any assumption. Payback, monthly savings, and 5-year cash position recompute instantly. All defaults are editable estimates." />
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4">
        <Panel className="p-6 space-y-6" data-testid="roi-inputs">
          <Overline>Assumptions</Overline>
          {INPUTS.map((d) => <Slider key={d[0]} def={d} value={v[d[0]]} onChange={set} />)}
          <p className="text-xs text-mute">Fixed: {KM_PER_TRIP} km per trip, ₹{fmt(SETUP)} one-time gateway & integration, 35% of resale attributed to better segregation, ₹2/kg tipping fee.</p>
        </Panel>
        <div className="space-y-4">
          <Panel className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line overflow-hidden">
            {[["Payback period", Number.isFinite(r.payback) ? `${r.payback.toFixed(1)}` : "Never", "months", C.volt, "roi-payback"],
              ["Net savings / month", `₹${fmt(r.net)}`, "", C.org, "roi-net-monthly"],
              ["Capex", `₹${fmt(r.capex)}`, "", C.txt, "roi-capex"],
              ["5-year net", `₹${fmt(r.fiveYr / 100000, 1)}`, "lakh", r.fiveYr > 0 ? C.volt : C.non, "roi-five-year"]].map(([l, val, u, col, id], i) => (
              <div key={l} className="p-5 sm:p-6 bg-surface" data-testid={id}>
                <Overline>{l}</Overline>
                <div className="font-mono text-3xl sm:text-4xl font-semibold mt-4 tabular-nums" style={{ color: col }}>{val}<span className="text-sm text-mute ml-1">{u}</span></div>
              </div>
            ))}
          </Panel>
          <Panel className="p-5 sm:p-6" data-testid="roi-chart">
            <Overline>Cumulative cash position / 60 months</Overline>
            <div className="h-64 mt-6">
              <ResponsiveContainer>
                <LineChart data={r.series} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid stroke={C.line} strokeDasharray="3 6" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: C.mute, fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} interval={11} />
                  <YAxis tickFormatter={(x) => `${(x / 100000).toFixed(0)}L`} tick={{ fill: C.mute, fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(x) => `₹${fmt(x)}`} labelFormatter={(m) => `Month ${m}`} contentStyle={{ background: C.ink, border: `1px solid ${C.line}`, borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                  <ReferenceLine y={0} stroke={C.mute} strokeDasharray="4 4" />
                  {Number.isFinite(r.payback) && r.payback <= 60 && <ReferenceLine x={Math.ceil(r.payback)} stroke={C.volt} label={{ value: "break-even", fill: C.volt, fontSize: 10, position: "top" }} />}
                  <Line type="linear" dataKey="cum" stroke={C.volt} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel className="p-5 sm:p-6" data-testid="roi-breakdown">
              <Overline>Monthly gross savings / ₹{fmt(r.gross)}</Overline>
              <div className="mt-6 space-y-4">
                {breakdown.map(([l, val, col]) => (
                  <div key={l}>
                    <div className="flex justify-between text-sm mb-2"><span className="text-mute">{l}</span><span className="font-mono">₹{fmt(val)}</span></div>
                    <div className="h-2 bg-line"><div className="h-2 transition-[width] duration-500" style={{ width: `${(val / r.gross) * 100}%`, background: col }} /></div>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel data-testid="roi-roadmap">
              <div className="p-5 sm:p-6 border-b border-line"><Overline>Scaling roadmap</Overline></div>
              {ROADMAP.map(([p, t, d, mult], i) => (
                <div key={p} className="grid grid-cols-[72px_1fr_auto] gap-4 items-center px-5 sm:px-6 py-4 border-b border-line last:border-b-0">
                  <span className="font-mono text-[10px] tracking-widest" style={{ color: i === 0 ? C.volt : C.mute }}>{p.toUpperCase()}</span>
                  <div><div className="font-semibold text-sm">{t}</div><div className="text-xs text-mute">{d}</div></div>
                  <span className="font-mono text-sm tabular-nums">₹{fmt((r.net * 12 * mult) / 100000, 1)}L<span className="text-mute text-xs">/yr</span></span>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
